import React, { useState, useEffect } from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import { Stack, Accordion, Typography, AccordionDetails, AccordionSummary } from '@mui/material';

import Tip from '../../../components/Tip';
import useLocales from '../../../hooks/useLocales';
import WorkflowDefinitionPicker from '../../shared/workflow/WorkflowDefinitionPicker';
import { getWorkflowBinding } from '../../../services/workflow/workflowBindingService';

type FormWorkflowBindingProps = {
  formTemplateId: number;
  disabled?: boolean;
  disabledReasonLocale?: string;
  onLoaded: (definitionNumber: number | '') => void;
  onSelectionChange: (definitionNumber: number | '') => void;
};

const FormWorkflowBinding: React.FC<FormWorkflowBindingProps> = ({
  formTemplateId,
  disabled = false,
  disabledReasonLocale,
  onLoaded,
  onSelectionChange,
}) => {
  const { t } = useLocales();
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedDefinitionNumber, setSelectedDefinitionNumber] = useState<number | ''>('');

  useEffect(() => {
    let isCurrent = true;

    getWorkflowBinding('form-template', formTemplateId)
      .then((state) => {
        if (!isCurrent) {
          return;
        }

        const current = state?.workflowDefinitionNumber ?? '';

        setSelectedDefinitionNumber(current);
        onLoaded(current);
      })
      .catch(() => undefined);

    return () => {
      isCurrent = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formTemplateId]);

  const handleChange = (definitionNumber: number | '') => {
    setSelectedDefinitionNumber(definitionNumber);
    onSelectionChange(definitionNumber);
  };

  return (
    <Accordion id="forms-workflow-accordion" sx={{ p: 3 }} expanded={!disabled && isExpanded}>
      <AccordionSummary
        expandIcon={disabled ? null : <ExpandMoreIcon />}
        sx={[
          { px: 3, cursor: disabled ? 'default' : 'pointer' },
          isExpanded && !disabled ? { py: 3 } : { py: 2 },
        ]}
        onClick={() => !disabled && setIsExpanded(!isExpanded)}
        id="wfBindAcc"
      >
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <Typography variant="h6" sx={{ color: disabled ? 'text.disabled' : 'text.primary' }}>
            {t('workflows.binding.title')}
          </Typography>

          {disabled && disabledReasonLocale && (
            <Tip longText titleLocale={disabledReasonLocale}>
              <InfoRoundedIcon color="secondary" fontSize="small" />
            </Tip>
          )}
        </Stack>
      </AccordionSummary>

      <AccordionDetails sx={{ width: '100%', py: 3, pl: 3, pr: 2, pt: 0 }}>
        <WorkflowDefinitionPicker
          hideHeader
          value={selectedDefinitionNumber}
          onChange={handleChange}
          helperText={t('workflows.binding.explainer')}
        />
      </AccordionDetails>
    </Accordion>
  );
};

export default FormWorkflowBinding;
